type InvoiceLineItem = {
  description: string;
  productName?: string | null;
  quantity: number;
  unitPrice: number;
  total: number;
};

type TaxLine = { name: string; rate: number; taxId?: string };

export type ExportableInvoice = {
  id: number;
  invoiceNumber?: string | null;
  title: string;
  status: string;
  customerName?: string | null;
  customerEmail?: string | null;
  customerPhone?: string | null;
  description?: string | null;
  total: number;
  subtotal?: number;
  taxAmount?: number;
  taxEnabled?: boolean;
  taxRate?: number;
  selectedTaxLines?: string | null;
  depositType?: "fixed" | "percent" | null;
  depositValue?: number | null;
  invoiceDate?: string | null;
  dueDate?: string | null;
  createdAt: string | Date;
  lineItems: InvoiceLineItem[];
};

function parseTaxLines(raw: string | null | undefined): TaxLine[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (t: any) => typeof t.name === "string" && typeof t.rate === "number",
    );
  } catch {
    return [];
  }
}

function formatDate(d: string | Date | null | undefined): string {
  if (!d) return "";
  const date = typeof d === "string" ? new Date(d) : d;
  if (isNaN(date.getTime())) return "";
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

function sanitizeIIF(value: string): string {
  return value.replace(/[\t\r\n]/g, " ").trim();
}

function escapeCSV(value: string): string {
  if (
    value.includes(",") ||
    value.includes('"') ||
    value.includes("\n") ||
    value.includes("\r")
  ) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function computeInvoiceTotals(invoice: ExportableInvoice) {
  const subtotal = invoice.lineItems.reduce((s, li) => s + li.total, 0);
  const taxEnabled = invoice.taxEnabled ?? false;
  const taxRate = invoice.taxRate ?? 0;
  const taxLines = parseTaxLines(invoice.selectedTaxLines);

  let taxAmount = 0;
  let taxName = "";
  if (taxEnabled) {
    if (taxLines.length > 0) {
      taxAmount = taxLines.reduce(
        (sum, tl) => sum + subtotal * (tl.rate / 100),
        0,
      );
      taxName = taxLines.map((tl) => tl.name).join("; ");
    } else if (taxRate > 0) {
      taxAmount = subtotal * (taxRate / 100);
      taxName = `Tax (${taxRate}%)`;
    }
  }

  const depositType = invoice.depositType;
  const depositValue = invoice.depositValue ?? 0;
  const grandTotal = subtotal + taxAmount;
  const depositAmount =
    depositValue > 0
      ? depositType === "fixed"
        ? depositValue
        : grandTotal * (depositValue / 100)
      : 0;

  return { subtotal, taxAmount, taxName, grandTotal, depositAmount };
}

export function generateQuickBooksCSV(invoices: ExportableInvoice[]): string {
  const headers = [
    "InvoiceNo",
    "Customer",
    "InvoiceDate",
    "DueDate",
    "ItemDescription",
    "ItemQuantity",
    "ItemRate",
    "ItemAmount",
    "TaxName",
    "TaxAmount",
    "Deposit",
    "Memo",
  ];

  const rows: string[][] = [];

  for (const inv of invoices) {
    const invNum = inv.invoiceNumber || `INV-${inv.id}`;
    const customer = inv.customerName || "Unknown";
    const invDate = formatDate(inv.invoiceDate || inv.createdAt);
    const dueDate = formatDate(inv.dueDate);
    const { taxAmount, taxName, depositAmount } = computeInvoiceTotals(inv);
    const memo = inv.description || "";

    if (inv.lineItems.length === 0) {
      rows.push([
        invNum,
        customer,
        invDate,
        dueDate,
        "",
        "",
        "",
        "",
        taxName,
        taxAmount > 0 ? taxAmount.toFixed(2) : "",
        depositAmount > 0 ? depositAmount.toFixed(2) : "",
        memo,
      ]);
    } else {
      inv.lineItems.forEach((li, idx) => {
        const desc = li.productName
          ? `${li.productName} - ${li.description}`
          : li.description;
        rows.push([
          invNum,
          customer,
          invDate,
          dueDate,
          desc,
          li.quantity.toString(),
          li.unitPrice.toFixed(2),
          li.total.toFixed(2),
          idx === 0 ? taxName : "",
          idx === 0 && taxAmount > 0 ? taxAmount.toFixed(2) : "",
          idx === 0 && depositAmount > 0 ? depositAmount.toFixed(2) : "",
          idx === 0 ? memo : "",
        ]);
      });
    }
  }

  const csvLines = [
    headers.map(escapeCSV).join(","),
    ...rows.map((row) => row.map(escapeCSV).join(",")),
  ];

  return csvLines.join("\r\n") + "\r\n";
}

export function generateQuickBooksIIF(invoices: ExportableInvoice[]): string {
  const lines: string[] = [];

  lines.push(
    [
      "!TRNS",
      "TRNSID",
      "TRNSTYPE",
      "DATE",
      "ACCNT",
      "NAME",
      "CLASS",
      "AMOUNT",
      "DOCNUM",
      "MEMO",
      "DUEDATE",
    ].join("\t"),
  );
  lines.push(
    [
      "!SPL",
      "SPLID",
      "TRNSTYPE",
      "DATE",
      "ACCNT",
      "NAME",
      "CLASS",
      "AMOUNT",
      "DOCNUM",
      "MEMO",
      "QNTY",
      "PRICE",
    ].join("\t"),
  );
  lines.push("!ENDTRNS");

  for (const inv of invoices) {
    const invNum = sanitizeIIF(inv.invoiceNumber || `INV-${inv.id}`);
    const customer = sanitizeIIF(inv.customerName || "Unknown");
    const invDate = formatDate(inv.invoiceDate || inv.createdAt);
    const dueDate = formatDate(inv.dueDate);
    const { taxAmount, grandTotal } = computeInvoiceTotals(inv);
    const memo = sanitizeIIF(inv.description || "");

    lines.push(
      [
        "TRNS",
        "",
        "INVOICE",
        invDate,
        "Accounts Receivable",
        customer,
        "",
        grandTotal.toFixed(2),
        invNum,
        memo,
        dueDate,
      ].join("\t"),
    );

    for (const li of inv.lineItems) {
      const desc = sanitizeIIF(
        li.productName
          ? `${li.productName} - ${li.description}`
          : li.description,
      );
      lines.push(
        [
          "SPL",
          "",
          "INVOICE",
          invDate,
          "Services",
          customer,
          "",
          (-li.total).toFixed(2),
          invNum,
          desc,
          (-li.quantity).toString(),
          li.unitPrice.toFixed(2),
        ].join("\t"),
      );
    }

    if (taxAmount > 0) {
      lines.push(
        [
          "SPL",
          "",
          "INVOICE",
          invDate,
          "Sales Tax Payable",
          customer,
          "",
          (-taxAmount).toFixed(2),
          invNum,
          "Tax",
          "",
          "",
        ].join("\t"),
      );
    }

    lines.push("ENDTRNS");
  }

  return lines.join("\r\n") + "\r\n";
}

export function downloadFile(
  content: string,
  filename: string,
  mimeType: string,
) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
